import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Switch,
  FormControlLabel, CircularProgress, Box, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

const defaultForm = { code: '', type: 'percent', value: '', minAmount: '', expiresAt: '', maxUses: '' };

export default function AdminDiscountsPage() {
  const { authHeader } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    axios.get(`${API}/discounts`, { headers: authHeader() })
      .then(r => setDiscounts(r.data.discounts || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreate = async () => {
    setError('');
    setSaving(true);
    try {
      await axios.post(`${API}/discounts`, form, { headers: authHeader() });
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d) => {
    await axios.patch(`${API}/discounts/${d.id}`, { active: !d.active }, { headers: authHeader() });
    setDiscounts(prev => prev.map(x => x.id === d.id ? { ...x, active: !x.active } : x));
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm('Eliminare questo sconto?')) return;
    await axios.delete(`${API}/discounts/${id}`, { headers: authHeader() });
    setDiscounts(prev => prev.filter(x => x.id !== id));
  };

  if (loading) return <Container sx={{ pt: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Codici Sconto</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Nuovo sconto
        </Button>
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Codice</TableCell>
              <TableCell>Sconto</TableCell>
              <TableCell>Min. ordine</TableCell>
              <TableCell>Utilizzi</TableCell>
              <TableCell>Scadenza</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {discounts.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Nessun codice sconto</TableCell></TableRow>
            )}
            {discounts.map(d => (
              <TableRow key={d.id} hover>
                <TableCell><Typography fontFamily="monospace" fontWeight="bold">{d.code}</Typography></TableCell>
                <TableCell>
                  {d.type === 'percent' ? `${d.value}%` : `€${d.value}`}
                </TableCell>
                <TableCell>{d.min_amount > 0 ? `€${d.min_amount}` : '—'}</TableCell>
                <TableCell>
                  {d.used_count}{d.max_uses ? `/${d.max_uses}` : ''}
                </TableCell>
                <TableCell>
                  {d.expires_at ? new Date(d.expires_at).toLocaleDateString('it-IT') : '—'}
                </TableCell>
                <TableCell>
                  <Switch checked={d.active} onChange={() => toggleActive(d)} size="small" />
                </TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={() => deleteDiscount(d.id)}>
                    <DeleteIcon fontSize="small" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo codice sconto</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Codice (es. ESTATE20)" name="code" value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            sx={{ mb: 2 }} required />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tipo</InputLabel>
            <Select name="type" value={form.type} onChange={handleChange} label="Tipo">
              <MenuItem value="percent">Percentuale (%)</MenuItem>
              <MenuItem value="fixed">Importo fisso (€)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label={form.type === 'percent' ? 'Percentuale (es. 10)' : 'Importo €'}
            name="value" type="number" value={form.value} onChange={handleChange} sx={{ mb: 2 }} required />
          <TextField fullWidth label="Importo minimo ordine (€)" name="minAmount" type="number"
            value={form.minAmount} onChange={handleChange} sx={{ mb: 2 }} />
          <TextField fullWidth label="Data scadenza" name="expiresAt" type="date"
            value={form.expiresAt} onChange={handleChange} sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Max utilizzi (vuoto = illimitato)" name="maxUses" type="number"
            value={form.maxUses} onChange={handleChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
